import { Component, OnInit, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { TaxService } from 'src/app/services/tax.service';
import { UnionDomain } from 'src/app/services/union.domain';
import { SubdomainService } from 'src/app/services/subdomain.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-bulk-tax-collection',
  templateUrl: './bulk-tax-collection.component.html',
  styleUrls: ['./bulk-tax-collection.component.scss'],
})
export class BulkTaxCollectionComponent implements OnInit, OnDestroy {
  $subscriptions: Subscription[] = [];
  ExcelData: any[] = [];
  fileName: any = 'BulkTaxCollectionSample.xlsx';
  isUploading: boolean = false;
  financialYear: number = new Date().getFullYear();
  yearList: number[] = [];
  allowDuplicates: boolean = false;
  autoCalculate: boolean = true;

  // Sample data for Excel template
  sampleData = [
    {
      'QR Code': 'sample-qr-code-1',
      'Holding Number': 'H001',
      'Amount': '500',
      'Auto Calculate': 'TRUE',
      'Allow Duplicate': 'FALSE',
      'Notes': 'Sample entry 1'
    },
    {
      'QR Code': 'sample-qr-code-2', 
      'Holding Number': 'H002',
      'Amount': '750',
      'Auto Calculate': 'FALSE',
      'Allow Duplicate': 'FALSE',
      'Notes': 'Sample entry 2'
    }
  ];

  constructor(
    private taxService: TaxService,
    private toastr: ToastrService,
    private unionDomain: UnionDomain,
    private subdomainService: SubdomainService
  ) {}

  ngOnInit(): void {
    this.ExcelData = [];
    this.fileName = 'BulkTaxCollectionSample.xlsx';
    
    // Generate year list for dropdown (current year and 4 previous years)
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) {
      this.yearList.push(currentYear - i);
    }
  }

  // Helper method to format year as financial year (BD tradition)
  formatFinancialYear(year: number): string {
    return `${year}-${year + 1}`;
  }

  ngOnDestroy(): void {
    this.$subscriptions.forEach((s) => s.unsubscribe());
  }

  ReadExcel(event: any) {
    let file = event.target.files[0];
    if (!file) return;

    let fileReader = new FileReader();
    fileReader.readAsBinaryString(file);
    fileReader.onload = (e) => {
      try {
        var workBook = XLSX.read(fileReader.result, { type: 'binary' });
        var sheetNames = workBook.SheetNames;
        this.ExcelData = XLSX.utils.sheet_to_json(workBook.Sheets[sheetNames[0]]);
        
        if (this.ExcelData.length === 0) {
          this.toastr.warning('এক্সেল ফাইলে কোন ডেটা পাওয়া যায়নি।');
          return;
        }
        
        this.toastr.success(`${this.ExcelData.length} টি রেকর্ড পড়া হয়েছে।`);
        console.log('Excel Data:', this.ExcelData);
      } catch (error) {
        this.toastr.error('এক্সেল ফাইল পড়তে সমস্যা হয়েছে।');
        console.error('Excel read error:', error);
      }
    };
  }

  exportSampleToExcel() {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.sampleData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sample');
    XLSX.writeFile(wb, 'BulkTaxCollectionTemplate.xlsx');
    this.toastr.info('নমুনা এক্সেল ফাইল ডাউনলোড হয়েছে।');
  }

  exportCurrentDataToExcel() {
    if (this.ExcelData.length === 0) {
      this.toastr.warning('কোন ডেটা নেই এক্সপোর্ট করার জন্য।');
      return;
    }
    
    let element = document.getElementById('tableId');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TaxCollection');
    XLSX.writeFile(wb, this.fileName);
  }

  processExcelData(): any[] {
    return this.ExcelData.map((row, index) => {
      // Handle different possible column names
      const qrCode = row['QR Code'] || row['qr_code'] || row['QRCode'] || '';
      const holdingNumber = row['Holding Number'] || row['holding_number'] || row['HoldingNumber'] || '';
      const amount = row['Amount'] || row['amount'] || '';
      const autoCalc = row['Auto Calculate'] || row['auto_calculate'] || row['AutoCalculate'] || this.autoCalculate;
      const allowDup = row['Allow Duplicate'] || row['allow_duplicate'] || row['AllowDuplicate'] || this.allowDuplicates;
      
      return {
        qr_code: qrCode ? qrCode.toString().trim() : '',
        holding_number: holdingNumber ? holdingNumber.toString().trim() : '',
        amount: amount ? parseInt(amount.toString()) : 0,
        auto_calculate: this.parseBoolean(autoCalc),
        allow_duplicate: this.parseBoolean(allowDup),
        row_index: index + 1
      };
    });
  }

  parseBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1';
    }
    return !!value;
  }

  validateData(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (this.ExcelData.length === 0) {
      errors.push('কোন ডেটা আপলোড করা হয়নি।');
      return { isValid: false, errors };
    }
    
    const processedData = this.processExcelData();
    
    processedData.forEach((row, index) => {
      if (!row.qr_code && !row.holding_number) {
        errors.push(`রো ${index + 1}: QR Code অথবা Holding Number প্রয়োজন।`);
      }
      
      if (!row.auto_calculate && (!row.amount || row.amount <= 0)) {
        errors.push(`রো ${index + 1}: Amount প্রয়োজন অথবা Auto Calculate চালু করুন।`);
      }
    });
    
    return { isValid: errors.length === 0, errors };
  }

  uploadToDatabase = (): void => {
    if (this.isUploading) return;
    
    // Validate data first
    const validation = this.validateData();
    if (!validation.isValid) {
      validation.errors.forEach(error => this.toastr.error(error));
      return;
    }
    
    this.isUploading = true;
    
    const payload = {
      taxCollections: this.processExcelData(),
      subdomain: this.subdomainService.getSubdomain(),
      financial_year: this.financialYear
    };
    
    console.log('Sending payload:', payload);
    
    this.$subscriptions.push(
      this.taxService.bulkTaxCollectionFromExcel(payload).subscribe({
        next: (res) => {
          this.isUploading = false;
          this.toastr.success(res.message);
          
          // Show errors if any
          if (res.errors && res.errors.length > 0) {
            this.toastr.warning(`${res.errors.length} টি সমস্যা পাওয়া গেছে। বিস্তারিত কনসোলে দেখুন।`);
            console.log('Processing errors:', res.errors);
          }
          
          // Reset form
          this.ngOnInit();
          
          // Clear file input
          const fileInput = document.getElementById('excelFileInput') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
        },
        error: (err) => {
          this.isUploading = false;
          this.toastr.error(err.error?.message || 'অপারেশন সম্পন্ন হয়নি।');
          console.error('Upload error:', err);
        },
      })
    );
  };

  clearData(): void {
    this.ExcelData = [];
    const fileInput = document.getElementById('excelFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.toastr.info('ডেটা ক্লিয়ার করা হয়েছে।');
  }
} 